import Cookies from 'js-cookie';
import { configSettings } from '@/lib/config';
import { AD_PIXEL_IDS, ADS_ANALYTIC_EVENTS, LINKEDIN_CONVERSION_IDS } from '@/lib/constants';
import axios from 'axios';

// Module-level flags to prevent multiple initializations
let isInitializing = false;
let isInitialized = false;

/**
 * Initialize all ad tracking pixels
 * Only runs in production environment
 * Includes guards to prevent multiple simultaneous initializations
 */
export function initializeAdsTrackers(): void {
    if (configSettings.environment !== 'production') {
        return;
    }

    // Guard: Don't initialize if already initialized or currently initializing
    if (isInitialized || isInitializing) {
        return;
    }

    isInitializing = true;

    // Initialize Facebook Pixel
    /* eslint-disable */
    (function (f: any, b: Document, e: string, v: string, n: any, t: any, s: any) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode!.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);

    window.fbq!('init', AD_PIXEL_IDS.FACEBOOK);

    // Initialize TikTok Pixel
    (function (w: any, d: Document, t: string) {
        w.TiktokAnalyticsObject = t;
        const ttq = (w[t] = w[t] || []);
        ttq.methods = [
            'page',
            'track',
            'identify',
            'instances',
            'debug',
            'on',
            'off',
            'once',
            'ready',
            'alias',
            'group',
            'enableCookie',
            'disableCookie',
        ];
        ttq.setAndDefer = function (t: any, e: string) {
            t[e] = function () {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            };
        };
        for (let i = 0; i < ttq.methods.length; i++) {
            ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        ttq.instance = function (t: string) {
            const e = ttq._i[t] || [];
            for (let n = 0; n < ttq.methods.length; n++) {
                ttq.setAndDefer(e, ttq.methods[n]);
            }
            return e;
        };
        ttq.load = function (e: string, n?: Record<string, any>) {
            const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
            ttq._i = ttq._i || {};
            ttq._i[e] = [];
            ttq._i[e]._u = i;
            ttq._t = ttq._t || {};
            ttq._t[e] = +new Date();
            ttq._o = ttq._o || {};
            ttq._o[e] = n || {};
            const o = document.createElement('script');
            o.type = 'text/javascript';
            o.async = true;
            o.src = i + '?sdkid=' + e + '&lib=' + t;
            const a = document.getElementsByTagName('script')[0];
            if (a && a.parentNode) {
                a.parentNode.insertBefore(o, a);
            }
        };

        ttq.load(AD_PIXEL_IDS.TIKTOK);
        ttq.page();
    })(window, document, 'ttq');

    // Initialize LinkedIn Pixel
    (function () {
        const s = document.getElementsByTagName('body')[0];
        if (s && s.parentNode) {
            const b = document.createElement('script');
            b.type = 'text/javascript';
            b.async = true;
            b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
            s.parentNode.insertBefore(b, s);

            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(AD_PIXEL_IDS.LINKEDIN_PARTNER);
        }
    })();
    /* eslint-enable */

    isInitializing = false;
    isInitialized = true;
}

/**
 * Track event on Facebook Pixel
 */
export function trackFacebookEvent(
    event: string,
    data: Record<string, any> = {},
    eventIdData: Record<string, any> = {}
): boolean {
    if (window.fbq) {
        window.fbq('track', event, data, eventIdData);
        return true;
    }

    return false;
}

/**
 * Track event on TikTok Pixel
 */
export function trackTikTokEvent(
    event: string,
    data: Record<string, any> = {},
    eventIdData: Record<string, any> = {}
): boolean {
    if (window.ttq) {
        window.ttq.track(event, data, eventIdData);
        return true;
    }

    return false;
}

/**
 * Track event on LinkedIn Insight Tag
 */
export function trackLinkedInEvent(event: string, eventId?: string): boolean {
    if (window.lintrk) {
        try {
            const defaultConversionId = LINKEDIN_CONVERSION_IDS.OTHER;
            let conversionId: number = defaultConversionId;

            // Map event name to conversion ID
            if (event in ADS_ANALYTIC_EVENTS) {
                const conversionKey = Object.keys(ADS_ANALYTIC_EVENTS).find(
                    key => ADS_ANALYTIC_EVENTS[key as keyof typeof ADS_ANALYTIC_EVENTS] === event
                ) as keyof typeof LINKEDIN_CONVERSION_IDS | undefined;

                if (conversionKey && conversionKey in LINKEDIN_CONVERSION_IDS) {
                    const mappedId = LINKEDIN_CONVERSION_IDS[conversionKey];
                    if (typeof mappedId === 'number') {
                        conversionId = mappedId;
                    }
                }
            }

            const payload: { conversion_id: number; event_id?: string } = {
                conversion_id: conversionId,
            };

            if (eventId) {
                payload.event_id = eventId;
            }

            window.lintrk('track', payload);
            return true;
        } catch (e) {
            console.error('LinkedIn tracking error:', e);
            return false;
        }
    }

    return false;
}

/**
 * Track conversion event via API and frontend pixels
 * Uses event deduplication with server-generated event_id
 */
export async function trackConversionEvent(
    event: string,
    data: Record<string, any> = {},
    serverData: Record<string, any> = {}
): Promise<void> {
    if (configSettings.environment !== 'production') {
        return;
    }

    let serverPayload: Record<string, any> = {
        event_name: event,
        url: window.location.href,
    };

    // Add Facebook tracking cookies
    const fbc = Cookies.get('_fbc');
    const fbp = Cookies.get('_fbp');
    const ttclid = Cookies.get('ttclid');

    if (fbc || fbp) {
        serverPayload = { ...serverPayload, fbc, fbp };
    }

    if (ttclid) {
        serverPayload = { ...serverPayload, ttclid };
    }

    if (Object.keys(data).length !== 0) {
        serverPayload.custom_data = data;
    }

    serverPayload = { ...serverPayload, ...serverData };

    try {
        const response = await axios.post('/v1/analytics/track-capi-event', serverPayload);
        const eventId = response?.data?.event_id;
        const ttEventName = response?.data?.tt_event_name;

        // Track with event ID for deduplication
        trackFacebookEvent(event, data, { eventID: eventId });
        trackTikTokEvent(ttEventName || event, data, { eventID: eventId });
        trackLinkedInEvent(event, eventId);
    } catch (error) {
        console.error('Server-side tracking failed, falling back to client-side only:', error);
        // Fallback to client-side tracking without event ID
        trackFacebookEvent(event, data);
        trackTikTokEvent(event, data);
        trackLinkedInEvent(event);
    }
}
